import {
  ContractFamilyDocumentFamilyStatus,
  ContractFamilyDocumentResponse,
  ContractFamilyListItem,
  ContractFamilyOutcomeReason,
  MatchCandidateResponse,
  ReviewQueueItem,
} from "@/types/contract-family";

export type ContractFamilyStatusTone = "neutral" | "success" | "warning" | "danger" | "info";

export interface ContractFamilyDocumentViewModel {
  familyId: string;
  documentId: string;
  fseId: string | null;
  displayName: string;
  role: string;
  roleLabel: string;
  familyStatus: ContractFamilyDocumentFamilyStatus;
  statusLabel: string;
  statusTone: ContractFamilyStatusTone;
  parentDocumentId: string | null;
  source: string;
  reviewStatus: string;
  ingestionSummary?: string | null;
}

export interface ContractFamilyGroupViewModel {
  familyId: string;
  name: string;
  rootDocumentId: string | null;
  status: string | null;
  master: ContractFamilyDocumentViewModel | null;
  documents: ContractFamilyDocumentViewModel[];
  childDocuments: ContractFamilyDocumentViewModel[];
  documentCount: number;
  statusCounts: Record<string, number>;
  reviewNeeded: boolean;
  isMasterOnly: boolean;
}

export interface ContractFamiliesViewModel {
  families: ContractFamilyGroupViewModel[];
  ungroupedDocuments: ContractFamilyDocumentViewModel[];
  ambiguousDocuments: ContractFamilyDocumentViewModel[];
  reviewDocumentIds: string[];
  reviewCount: number;
}

const UNGROUPED_STATUSES = new Set(["orphan", "unlinked"]);
const REVIEW_STATUSES = new Set(["ambiguous"]);

const hasCandidateReasoning = (candidate: MatchCandidateResponse) => Boolean(candidate.reasoning?.trim());
const hasCandidateSignals = (candidate: MatchCandidateResponse) => Boolean(candidate.signals_json);

export const deduplicateReviewCandidates = (candidates: MatchCandidateResponse[]): MatchCandidateResponse[] => {
  const selected = new Map<string, MatchCandidateResponse>();

  for (const candidate of candidates) {
    const current = selected.get(candidate.candidate_master_document_id);
    const candidateStrength = [candidate.confidence, hasCandidateReasoning(candidate), hasCandidateSignals(candidate)];
    const currentStrength = current
      ? [current.confidence, hasCandidateReasoning(current), hasCandidateSignals(current)]
      : null;
    if (
      currentStrength === null ||
      candidateStrength[0] > currentStrength[0] ||
      (candidateStrength[0] === currentStrength[0] && candidateStrength[1] > currentStrength[1]) ||
      (candidateStrength[0] === currentStrength[0] &&
        candidateStrength[1] === currentStrength[1] &&
        candidateStrength[2] > currentStrength[2])
    ) {
      selected.set(candidate.candidate_master_document_id, candidate);
    }
  }

  return Array.from(selected.values());
};

export const formatContractFamilyRole = (role: string) =>
  role
    .toLowerCase()
    .split("_")
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const formatContractFamilyStatus = (status: string) =>
  status
    .toLowerCase()
    .split("_")
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const GENERIC_REVIEW_REASON = "This document needs review before it can be added to a contract family.";

export const formatContractFamilyOutcomeReason = (reason: ContractFamilyOutcomeReason | null): string => {
  switch (reason) {
    case "predefined_no_master":
      return "This uploaded folder does not contain a master contract.";
    case "predefined_multiple_masters":
      return "This uploaded folder contains multiple possible master contracts.";
    case "predefined_root_unassigned":
      return "This document was uploaded outside a contract family folder.";
    case "predefined_extraction_failed":
      return "Contract details could not be extracted, so this document could not be grouped.";
    default:
      return GENERIC_REVIEW_REASON;
  }
};

export const getContractFamilyStatusTone = (status: string): ContractFamilyStatusTone => {
  switch (status) {
    case "linked":
      return "success";
    case "orphan":
    case "ambiguous":
      return "warning";
    case "unlinked":
      return "neutral";
    case "failed":
    case "matching_failed":
    case "extraction_failed":
      return "danger";
    default:
      return "info";
  }
};

const displayDocumentName = (document: Pick<ContractFamilyDocumentResponse, "name" | "document_id">) =>
  document.name?.trim() || document.document_id;

const displayFamilyName = (family: ContractFamilyListItem) =>
  family.name?.trim() || family.root_document_id || family.family_id;

const isUnassignedFamily = (family: ContractFamilyListItem) =>
  family.root_document_id === null || family.family_id.startsWith("unassigned:");

const toDocumentViewModel = (
  family: Pick<ContractFamilyListItem, "family_id">,
  document: ContractFamilyDocumentResponse,
): ContractFamilyDocumentViewModel => ({
  familyId: family.family_id,
  documentId: document.document_id,
  fseId: document.fse_id,
  displayName: displayDocumentName(document),
  role: document.role,
  roleLabel: formatContractFamilyRole(document.role),
  familyStatus: document.family_status,
  statusLabel: formatContractFamilyStatus(document.family_status),
  statusTone: getContractFamilyStatusTone(document.family_status),
  parentDocumentId: document.parent_document_id,
  source: document.source,
  reviewStatus: document.review_status,
  ingestionSummary: document.ingestion_summary,
});

const reviewItemToDocumentViewModel = (item: ReviewQueueItem): ContractFamilyDocumentViewModel => ({
  familyId: `review:${item.document_id}`,
  documentId: item.document_id,
  fseId: null,
  displayName: item.name?.trim() || item.document_id,
  role: item.role,
  roleLabel: formatContractFamilyRole(item.role),
  familyStatus: item.family_status,
  statusLabel: formatContractFamilyStatus(item.family_status),
  statusTone: getContractFamilyStatusTone(item.family_status),
  parentDocumentId: null,
  source: "review",
  reviewStatus: item.review_status,
  ingestionSummary: undefined,
});

const isReviewDocument = (document: ContractFamilyDocumentViewModel) =>
  REVIEW_STATUSES.has(document.familyStatus) || document.reviewStatus === "pending";

export const buildContractFamiliesViewModel = (
  families: ContractFamilyListItem[],
  reviewItems: ReviewQueueItem[] = [],
): ContractFamiliesViewModel => {
  const activeFamilies: ContractFamilyGroupViewModel[] = [];
  const ungroupedDocuments: ContractFamilyDocumentViewModel[] = [];
  const ambiguousDocuments: ContractFamilyDocumentViewModel[] = [];
  const reviewDocumentIds: string[] = [];
  const seenReviewDocumentIds = new Set<string>();

  const addReviewDocument = (document: ContractFamilyDocumentViewModel) => {
    if (seenReviewDocumentIds.has(document.documentId)) {
      return;
    }

    seenReviewDocumentIds.add(document.documentId);
    ambiguousDocuments.push(document);
    reviewDocumentIds.push(document.documentId);
  };

  for (const family of families) {
    const documents = family.documents.map(document => toDocumentViewModel(family, document));

    for (const document of documents) {
      if (isReviewDocument(document)) {
        addReviewDocument(document);
      }
    }

    if (isUnassignedFamily(family)) {
      ungroupedDocuments.push(
        ...documents.filter(document => UNGROUPED_STATUSES.has(document.familyStatus) && !isReviewDocument(document)),
      );
      continue;
    }

    const master =
      documents.find(document => document.documentId === family.root_document_id) ??
      documents.find(document => document.role === "MASTER") ??
      null;
    const childDocuments = master
      ? documents.filter(document => document.documentId !== master.documentId)
      : documents.filter(document => document.role !== "MASTER");

    activeFamilies.push({
      familyId: family.family_id,
      name: displayFamilyName(family),
      rootDocumentId: family.root_document_id,
      status: family.status,
      master,
      documents,
      childDocuments,
      documentCount: documents.length,
      statusCounts: family.status_counts,
      reviewNeeded: family.review_needed,
      isMasterOnly: documents.length === 1 && master !== null,
    });
  }

  for (const item of reviewItems) {
    addReviewDocument(reviewItemToDocumentViewModel(item));
  }

  return {
    families: activeFamilies,
    ungroupedDocuments,
    ambiguousDocuments,
    reviewDocumentIds,
    reviewCount: reviewDocumentIds.length,
  };
};

export const filterContractFamiliesViewModel = (
  viewModel: ContractFamiliesViewModel,
  query: string,
): ContractFamiliesViewModel => {
  const normalized = query.trim().toLocaleLowerCase();
  if (!normalized) return viewModel;

  const includesQuery = (value: string) => value.toLocaleLowerCase().includes(normalized);
  const families = viewModel.families.filter(
    family => includesQuery(family.name) || family.documents.some(document => includesQuery(document.displayName)),
  );
  const ungroupedDocuments = viewModel.ungroupedDocuments.filter(document => includesQuery(document.displayName));
  const visibleDocumentIds = new Set([
    ...families.flatMap(family => family.documents.map(document => document.documentId)),
    ...ungroupedDocuments.map(document => document.documentId),
  ]);
  const ambiguousDocuments = viewModel.ambiguousDocuments.filter(document =>
    visibleDocumentIds.has(document.documentId),
  );
  const reviewDocumentIds = viewModel.reviewDocumentIds.filter(documentId => visibleDocumentIds.has(documentId));

  return {
    ...viewModel,
    families,
    ungroupedDocuments,
    ambiguousDocuments,
    reviewDocumentIds,
    reviewCount: reviewDocumentIds.length,
  };
};
